/** The cursor states the app uses. Sourced from the board's CursorStyle enum
 *  (default/grab/grabbing), the crochet editor's crosshair, the Tailwind
 *  cursor-* utilities used in src/ (pointer/text/not-allowed/se-resize), and
 *  the rect-TBC gizmo canvas states (ns-resize/ew-resize/nesw-resize/
 *  nwse-resize/rotate).  Canvas-only tool states (pen/eraser/eraser-disabled)
 *  and the gizmo states have no native CSS keyword and use an explicit
 *  fallback. */
export const CURSOR_STATES = [
    'default',
    'pointer',
    'text',
    'grab',
    'grabbing',
    'crosshair',
    'not-allowed',
    'se-resize',
    'pen',
    'eraser',
    'eraser-disabled',
    'ns-resize',
    'ew-resize',
    'nesw-resize',
    'nwse-resize',
    'rotate',
] as const;

export type CursorState = (typeof CURSOR_STATES)[number];

export interface CursorSpec {
    /** @1x PNG url (served from public/cursors). */
    url: string;
    /** @2x PNG url, for retina (used only by static CSS). */
    url2x: string;
    /** Active pixel, in @1x coordinates. */
    hotspotX: number;
    hotspotY: number;
    /** Standard CSS keyword used as fallback. Equals the state name for
     *  native CSS cursor states; canvas-only states use an explicit keyword. */
    fallback: CursorState;
}

const spec = (
    state: CursorState,
    hotspotX: number,
    hotspotY: number,
    fallback: CursorState = state,
): CursorSpec => ({
    url: `/cursors/${state}.png`,
    url2x: `/cursors/${state}@2x.png`,
    hotspotX,
    hotspotY,
    fallback,
});

/** Single source of truth: filenames + hotspots. Hotspots are mirrored (by
 *  hand) in the static CSS block in src/style.css — keep them in sync. */
export const CURSOR_REGISTRY: Record<CursorState, CursorSpec> = {
    default: spec('default', 4, 3),
    pointer: spec('pointer', 10, 2),
    text: spec('text', 16, 16),
    grab: spec('grab', 16, 16),
    grabbing: spec('grabbing', 16, 16),
    crosshair: spec('crosshair', 16, 16),
    'not-allowed': spec('not-allowed', 16, 16),
    'se-resize': spec('se-resize', 16, 16),
    // Canvas-only states (set inline on the Pixi canvas; intentionally absent
    // from the src/style.css DOM block).
    pen: spec('pen', 7, 25, 'crosshair'),
    eraser: spec('eraser', 10, 25, 'crosshair'),
    'eraser-disabled': spec('eraser-disabled', 10, 25, 'not-allowed'),
    // Rect TBC gizmo cursors (canvas-only, like pen/eraser). The resize states
    // are native CSS keywords so they fall back to themselves; rotate has no
    // native keyword and falls back to grab.
    'ns-resize': spec('ns-resize', 16, 16),
    'ew-resize': spec('ew-resize', 16, 16),
    'nesw-resize': spec('nesw-resize', 16, 16),
    'nwse-resize': spec('nwse-resize', 16, 16),
    rotate: spec('rotate', 16, 16, 'grab'),
};

/** Inlined `data:` URIs for the canvas cursors, keyed by state. Populated by
 *  `preloadCursorDataUris`. Once filled, `buildCursorCss` emits a self-contained
 *  cursor so the browser has no external image to evict (see preload note). */
const dataUriCache = new Map<CursorState, string>();

/** Universally-supported `cursor` value: `url(...) x y, <keyword>`.
 *  Used by the canvas JS path. Retina is handled only in static CSS.
 *
 *  Prefers an inlined `data:` URI once preloaded — on memory-constrained
 *  devices (e.g. a Steam Deck) Chromium evicts the decoded image referenced by
 *  a network `url("/cursors/…")` and repaints the keyword fallback, so the
 *  canvas cursor silently reverts to the native arrow. An inline image can't be
 *  evicted. Falls back to the network url until the preload completes. */
export function buildCursorCss(state: CursorState): string {
    const s = CURSOR_REGISTRY[state];
    const href = dataUriCache.get(state) ?? s.url;
    return `url("${href}") ${s.hotspotX} ${s.hotspotY}, ${s.fallback}`;
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

let preloadPromise: Promise<void> | null = null;

/** Fetch every canvas cursor PNG once and inline it as a `data:` URI in
 *  `dataUriCache`, so `buildCursorCss` stops referencing external `/cursors/…`
 *  images that Chromium can evict mid-session (reverting the cursor to the
 *  native arrow). Idempotent — the first call does the work and subsequent
 *  calls return the same promise. A failed fetch leaves that state on its
 *  network-url fallback; nothing throws. */
export function preloadCursorDataUris(
    fetchImpl: typeof fetch = fetch,
): Promise<void> {
    if (preloadPromise) {
        return preloadPromise;
    }
    preloadPromise = (async () => {
        await Promise.all(
            CURSOR_STATES.map(async (state) => {
                try {
                    const res = await fetchImpl(CURSOR_REGISTRY[state].url);
                    if (!res.ok) {
                        return;
                    }
                    const bytes = new Uint8Array(await res.arrayBuffer());
                    dataUriCache.set(
                        state,
                        `data:image/png;base64,${bytesToBase64(bytes)}`,
                    );
                } catch {
                    // Keep the network-url fallback for this state.
                }
            }),
        );
    })();
    return preloadPromise;
}

/** Test-only: reset the preload cache + memoized promise between cases. */
export function __resetCursorPreloadForTest(): void {
    dataUriCache.clear();
    preloadPromise = null;
}
